import { useEffect, useRef, useState } from 'react';

/**
 * Hook for delayed unmounting of components with animation support.
 * Provides control over the mounting state and unmount timing to allow for smooth animations.
 *
 * @param mounted - Boolean indicating whether the component should be mounted
 * @param delay - Delay in milliseconds before unmounting the component
 * @returns Tuple containing:
 *   - isMounted: Boolean indicating if the component is currently mounted
 *   - willUnmount: Boolean indicating if the component is in the process of unmounting
 */
export function useDelayedUnmount(mounted: boolean, delay: number) {
  const [isMounted, setIsMounted] = useState(mounted);
  const [willUnmount, setWillUnmount] = useState(false);
  const timerRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (mounted) {
      setWillUnmount(false);
      setIsMounted(true);
      clearTimeout(timerRef.current);
    } else {
      setWillUnmount(true);

      timerRef.current = setTimeout(() => {
        setIsMounted(false);
      }, delay);
    }

    return () => clearTimeout(timerRef.current);
  }, [mounted, delay]);

  return [isMounted, willUnmount];
}
